import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Users, Settings2, Power, Wallet, Percent, SlidersHorizontal, Pencil } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { money, dateFmt } from "@/lib/format";
import { Switch } from "@/components/ui/switch";

const INCOME_TYPES = [
  { key: "roi", label: "ROI" },
  { key: "level", label: "Level" },
  { key: "matching", label: "Matching" },
  { key: "salary", label: "Salary" },
  { key: "royalty", label: "Royalty" },
  { key: "turnover", label: "Turnover" },
];

function ManageModal({ user, onClose, onChanged }) {
  const [tab, setTab] = useState("details");
  const [form, setForm] = useState({ name: user.name || "", username: user.username || "", email: user.email || "", phone: user.phone || "", deposit_address: user.deposit_address || "" });
  const [toggles, setToggles] = useState(() => {
    const base = {}; INCOME_TYPES.forEach((t) => (base[t.key] = user.income_toggles?.[t.key] !== false)); return base;
  });
  const [roi, setRoi] = useState(user.roi_override ?? "");
  const [adj, setAdj] = useState({ balance: "income", amount: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const [unameChk, setUnameChk] = useState({ checking: false, available: true, reason: "" });
  useEffect(() => {
    const val = (form.username || "").trim().toLowerCase();
    if (val === (user.username || "").toLowerCase()) { setUnameChk({ checking: false, available: true, reason: "Current username" }); return; }
    setUnameChk((s) => ({ ...s, checking: true }));
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/auth/username-available?u=${encodeURIComponent(val)}&exclude=${user.id}`);
        setUnameChk({ checking: false, available: data.available, reason: data.reason });
      } catch { setUnameChk({ checking: false, available: false, reason: "Check failed" }); }
    }, 400);
    return () => clearTimeout(t);
  }, [form.username, user.username, user.id]);

  const call = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); onChanged(); }
    catch (e) { toast.error(apiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const tabs = [
    { id: "details", label: "Details", icon: Pencil },
    { id: "status", label: "Status", icon: Power },
    { id: "incomes", label: "Incomes", icon: SlidersHorizontal },
    { id: "roi", label: "ROI", icon: Percent },
    { id: "balance", label: "Balance", icon: Wallet },
  ];

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose} data-testid="manage-user-modal">
      <div className="glass border border-amber-500/25 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-amber-500/15">
          <div>
            <div className="font-display font-bold text-lg">{user.name}</div>
            <div className="text-xs text-slate-400">{user.email} · <span className="font-mono-fin text-amber-300">{user.referral_code}</span></div>
          </div>
          <span className={`text-xs px-2.5 py-1 rounded-full ${user.active === false ? "bg-red-500/15 text-red-400" : "bg-emerald-500/15 text-emerald-400"}`} data-testid="user-status-badge">
            {user.active === false ? "De-active" : "Active"}
          </span>
        </div>

        <div className="flex gap-1 p-3 border-b border-amber-500/15 overflow-x-auto">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`manage-tab-${t.id}`}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${tab === t.id ? "gold-btn" : "text-slate-400 hover:text-amber-300"}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        <div className="p-5 space-y-4">
          {tab === "details" && (
            <div className="space-y-3" data-testid="tab-panel-details">
              <div>
                <label className="text-xs text-amber-400 uppercase tracking-wider">Username</label>
                <input data-testid="edit-username-input" value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value })}
                  className="mt-1 w-full bg-[#0D0E12] border border-amber-500/20 focus:border-amber-500 rounded-xl px-4 py-2.5 outline-none text-sm font-mono-fin" />
                <div data-testid="username-availability" className={`text-xs mt-1 ${unameChk.checking ? "text-slate-400" : unameChk.available ? "text-emerald-400" : "text-red-400"}`}>
                  {unameChk.checking ? "Checking…" : unameChk.reason}
                </div>
              </div>
              {[["name", "Name"], ["email", "Email"], ["phone", "Phone"], ["deposit_address", "Wallet (BEP20)"]].map(([k, l]) => (
                <div key={k}>
                  <label className="text-xs text-amber-400 uppercase tracking-wider">{l}</label>
                  <input data-testid={`edit-${k}-input`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    className="mt-1 w-full bg-[#0D0E12] border border-amber-500/20 focus:border-amber-500 rounded-xl px-4 py-2.5 outline-none text-sm" />
                </div>
              ))}
              <button data-testid="save-details-btn" disabled={busy || unameChk.checking || !unameChk.available}
                onClick={() => call(() => api.patch(`/admin/users/${user.id}`, form), "Details updated")}
                className="gold-btn px-5 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-50">Save Details</button>
            </div>
          )}

          {tab === "status" && (
            <div className="space-y-4" data-testid="tab-panel-status">
              <p className="text-sm text-slate-400">Deactivating an ID stops <b>all</b> income accrual on the next cycle. The user can still log in and view their portal.</p>
              <div className="flex gap-3">
                <button data-testid="activate-btn" disabled={busy || user.active !== false}
                  onClick={() => call(() => api.post(`/admin/users/${user.id}/active`, { active: true }), "ID activated")}
                  className="flex-1 gold-btn px-4 py-3 rounded-xl text-sm font-semibold disabled:opacity-40">Activate ID</button>
                <button data-testid="deactivate-btn" disabled={busy || user.active === false}
                  onClick={() => call(() => api.post(`/admin/users/${user.id}/active`, { active: false }), "ID de-activated")}
                  className="flex-1 px-4 py-3 rounded-xl text-sm font-semibold border border-red-500/40 text-red-400 hover:bg-red-500/10 disabled:opacity-40">De-activate ID</button>
              </div>
            </div>
          )}

          {tab === "incomes" && (
            <div className="space-y-3" data-testid="tab-panel-incomes">
              <p className="text-sm text-slate-400">Turn individual income streams ON/OFF. Applies from the next income cycle.</p>
              {INCOME_TYPES.map((t) => (
                <div key={t.key} className="flex items-center justify-between bg-[#0D0E12] border border-amber-500/15 rounded-xl px-4 py-3">
                  <span className="text-sm font-medium">{t.label} Income</span>
                  <Switch data-testid={`toggle-${t.key}`} checked={toggles[t.key]} onCheckedChange={(v) => setToggles({ ...toggles, [t.key]: v })} />
                </div>
              ))}
              <button data-testid="save-toggles-btn" disabled={busy}
                onClick={() => call(() => api.post(`/admin/users/${user.id}/income-toggles`, { toggles }), "Income toggles saved")}
                className="gold-btn px-5 py-2.5 rounded-xl text-sm font-semibold">Save Toggles</button>
            </div>
          )}

          {tab === "roi" && (
            <div className="space-y-3" data-testid="tab-panel-roi">
              <p className="text-sm text-slate-400">Override the daily ROI % for this ID (leave blank to use the package default). Applies from the next cycle.</p>
              <div>
                <label className="text-xs text-amber-400 uppercase tracking-wider">Daily ROI Override (%)</label>
                <input data-testid="roi-override-input" type="number" step="0.01" value={roi} onChange={(e) => setRoi(e.target.value)} placeholder="e.g. 0.50"
                  className="mt-1 w-full bg-[#0D0E12] border border-amber-500/20 focus:border-amber-500 rounded-xl px-4 py-2.5 outline-none text-sm" />
              </div>
              <div className="flex gap-3">
                <button data-testid="save-roi-btn" disabled={busy}
                  onClick={() => call(() => api.post(`/admin/users/${user.id}/roi-override`, { roi_override: roi === "" ? null : Number(roi) }), "ROI override saved")}
                  className="gold-btn px-5 py-2.5 rounded-xl text-sm font-semibold">Save Override</button>
                <button data-testid="clear-roi-btn" disabled={busy}
                  onClick={() => { setRoi(""); call(() => api.post(`/admin/users/${user.id}/roi-override`, { roi_override: null }), "Override cleared"); }}
                  className="px-5 py-2.5 rounded-xl text-sm font-semibold border border-amber-500/30 text-amber-300 hover:bg-amber-500/10">Clear</button>
              </div>
            </div>
          )}

          {tab === "balance" && (
            <div className="space-y-3" data-testid="tab-panel-balance">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-[#0D0E12] border border-amber-500/15 rounded-xl p-3"><div className="text-[10px] text-slate-500 uppercase">Deposit</div><div className="font-mono-fin text-sm">{money(user.deposit_balance || 0)}</div></div>
                <div className="bg-[#0D0E12] border border-amber-500/15 rounded-xl p-3"><div className="text-[10px] text-slate-500 uppercase">Income</div><div className="font-mono-fin text-sm text-emerald-400">{money(user.income_balance || 0)}</div></div>
                <div className="bg-[#0D0E12] border border-amber-500/15 rounded-xl p-3"><div className="text-[10px] text-slate-500 uppercase">Points</div><div className="font-mono-fin text-sm text-sky-400">{money(user.points_balance || 0)}</div></div>
              </div>
              <div>
                <label className="text-xs text-amber-400 uppercase tracking-wider">Balance</label>
                <select data-testid="adjust-balance-select" value={adj.balance} onChange={(e) => setAdj({ ...adj, balance: e.target.value })}
                  className="mt-1 w-full bg-[#0D0E12] border border-amber-500/20 focus:border-amber-500 rounded-xl px-4 py-2.5 outline-none text-sm">
                  <option value="income">Income</option><option value="deposit">Deposit</option><option value="points">Points</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-amber-400 uppercase tracking-wider">Amount (use − to deduct)</label>
                <input data-testid="adjust-amount-input" type="number" step="0.01" value={adj.amount} onChange={(e) => setAdj({ ...adj, amount: e.target.value })} placeholder="e.g. 100 or -50"
                  className="mt-1 w-full bg-[#0D0E12] border border-amber-500/20 focus:border-amber-500 rounded-xl px-4 py-2.5 outline-none text-sm" />
              </div>
              <div>
                <label className="text-xs text-amber-400 uppercase tracking-wider">Reason</label>
                <input data-testid="adjust-reason-input" value={adj.reason} onChange={(e) => setAdj({ ...adj, reason: e.target.value })} placeholder="Optional note"
                  className="mt-1 w-full bg-[#0D0E12] border border-amber-500/20 focus:border-amber-500 rounded-xl px-4 py-2.5 outline-none text-sm" />
              </div>
              <button data-testid="apply-adjust-btn" disabled={busy || adj.amount === "" || Number(adj.amount) === 0}
                onClick={() => call(() => api.post(`/admin/users/${user.id}/adjust-balance`, { balance: adj.balance, amount: Number(adj.amount), reason: adj.reason }), "Balance adjusted").then(() => setAdj({ ...adj, amount: "", reason: "" }))}
                className="gold-btn px-5 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-40">Apply Adjustment</button>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-amber-500/15 text-right">
          <button data-testid="close-manage-btn" onClick={onClose} className="px-5 py-2 rounded-xl text-sm text-slate-400 hover:text-amber-300">Close</button>
        </div>
      </div>
    </div>
  );
}

export default function AdminUsers() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [manage, setManage] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/users").then(({ data }) => {
      setRows(data);
      setManage((m) => (m ? data.find((r) => r.id === m.id) || null : null));
    }).catch(() => toast.error("Load failed"));
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter((r) => r.name.toLowerCase().includes(q.toLowerCase()) || r.email.toLowerCase().includes(q.toLowerCase()) || (r.referral_code || "").includes(q.toUpperCase()));

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <h1 className="font-display text-2xl font-bold flex items-center gap-2"><Users className="w-6 h-6 text-amber-400" /> Users ({rows.length})</h1>
        <input data-testid="user-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, code..."
          className="bg-[#0D0E12] border border-amber-500/20 focus:border-amber-500 rounded-xl px-4 py-2.5 outline-none text-sm w-64" />
      </div>

      <div className="glass rounded-2xl p-6 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-amber-400 uppercase text-xs tracking-wider border-b border-amber-500/20">
            <th className="text-left py-3">Name</th><th className="text-left py-3">Email</th><th className="text-left py-3">Code</th>
            <th className="text-center py-3">Status</th>
            <th className="text-right py-3">Deposit</th><th className="text-right py-3">Income</th><th className="text-center py-3">Rank</th><th className="text-right py-3">Manage</th>
          </tr></thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id} className="border-b border-slate-800/60 hover:bg-amber-500/5" data-testid={`user-row-${r.id}`}>
                <td className="py-3 font-medium">{r.name}</td>
                <td className="py-3 text-slate-400">{r.email}</td>
                <td className="py-3 font-mono-fin text-amber-300">{r.referral_code}</td>
                <td className="py-3 text-center">
                  <span className={`text-[10px] px-2 py-1 rounded-full ${r.active === false ? "bg-red-500/15 text-red-400" : "bg-emerald-500/15 text-emerald-400"}`}>
                    {r.active === false ? "OFF" : "ON"}
                  </span>
                </td>
                <td className="py-3 text-right font-mono-fin">{money(r.deposit_balance || 0)}</td>
                <td className="py-3 text-right font-mono-fin text-emerald-400">{money(r.income_balance || 0)}</td>
                <td className="py-3 text-center"><span className="text-xs px-2 py-1 rounded bg-amber-500/15 text-amber-400">R{r.rank || 0}</span></td>
                <td className="py-3 text-right">
                  <button data-testid={`manage-btn-${r.id}`} onClick={() => setManage(r)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-amber-500/30 text-amber-300 hover:bg-amber-500/10">
                    <Settings2 className="w-3.5 h-3.5" /> Manage
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {manage && <ManageModal user={manage} onClose={() => setManage(null)} onChanged={load} />}
    </div>
  );
}
